// devicesApi — typed list/patch for /v1/admin/devices (Route 11).
import { apiFetch } from "../../lib/api-client.js";

export type DeviceStatus = "active" | "revoked";

export interface DeviceRow {
  id: string;
  tenant_id: string;
  officer_id: string;
  officer_name: string;
  device_model: string | null;
  os: string | null;
  user_agent: string | null;
  last_ip_hash: string | null;
  webauthn_credential_id: string | null;
  webauthn_bound: boolean;
  enrolled_at: string;
  last_seen_at: string | null;
  status: DeviceStatus;
  updated_at: string;
}

export interface DeviceListResponse {
  items: DeviceRow[];
  total: number;
  correlation_id: string | null;
}

export interface DevicesQuery {
  tenantId?: string | null;
  status?: DeviceStatus | null;
  /** Filter to one officer's devices — used by the Users page drawer's
   *  「裝置」 tab to avoid pulling the whole tenant list. */
  officerId?: string | null;
  limit?: number | null;
  offset?: number | null;
}

export class DevicesApiError extends Error {
  status: number; code: string; correlationId: string | null;
  constructor(status: number, code: string, correlationId: string | null) {
    super(code); this.status = status; this.code = code; this.correlationId = correlationId;
  }
}
export function friendlyDevicesError(err: unknown): string {
  if (err instanceof DevicesApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403) return "您目前的角色不能修改裝置。";
    if (err.status === 404) return "找不到此裝置。";
    if (err.status === 400 && err.code === "tenant_id_required") return "尚未選定操作的單位。";
    return "裝置資料暫時無法存取。";
  }
  return "連線中斷，請檢查網路後再試。";
}
async function asError(r: Response): Promise<DevicesApiError> {
  let code = "request_failed"; let cid: string | null = r.headers.get("x-correlation-id");
  try { const b = (await r.json()) as { error?: string; correlation_id?: string }; code = b.error ?? code; cid = b.correlation_id ?? cid; } catch {}
  return new DevicesApiError(r.status, code, cid);
}

/** Legacy positional signature kept for backwards compat — new callers
 *  should pass a `DevicesQuery` object so limit/offset are first-class. */
export async function listAdminDevices(
  params?: DevicesQuery | string | null,
  statusHint?: DeviceStatus | null,
): Promise<DeviceListResponse> {
  const opts: DevicesQuery = typeof params === "string" || params === null || params === undefined
    ? { tenantId: params as string | null | undefined, status: statusHint ?? null }
    : params;
  const qs = new URLSearchParams();
  if (opts.tenantId) qs.set("tenant_id", opts.tenantId);
  if (opts.status) qs.set("status", opts.status);
  if (opts.officerId) qs.set("officer_id", opts.officerId);
  if (opts.limit != null) qs.set("limit", String(opts.limit));
  if (opts.offset != null) qs.set("offset", String(opts.offset));
  const r = await apiFetch(`/v1/admin/devices${qs.toString() ? `?${qs}` : ""}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as DeviceListResponse;
}

export async function patchDeviceStatus(id: string, status: DeviceStatus, tenantId?: string | null): Promise<void> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/admin/devices/${encodeURIComponent(id)}${qs}`, {
    method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify({ status }), credentials: "include",
  });
  if (!r.ok) throw await asError(r);
}
